import React, { useState } from 'react'
import { useMarketInsight } from '../hooks/useMarketInsight'

// Helper for trend badge configuration
const getTrendConfig = (trend: 'up' | 'down' | 'stable' | undefined) => {
    switch (trend) {
        case 'up':
            return { color: 'success', label: 'In crescita' }
        case 'down':
            return { color: 'danger', label: 'In calo' }
        case 'stable':
            return { color: 'warning', label: 'Stabile' }
        default:
            return { color: 'secondary', label: 'N/A' }
    }
}

// Simple date formatter
const formatDate = (dateString?: string) => {
    if (!dateString) return ''
    const date = new Date(dateString)
    return new Intl.DateTimeFormat('it-IT', { day: 'numeric', month: 'long', hour: '2-digit', minute: '2-digit' }).format(date)
}

export const DashboardMarketInsightWidget: React.FC = () => {
    const { data, isAvailable, loading } = useMarketInsight()
    const [showDetail, setShowDetail] = useState(false)

    // 1. Loading State
    if (loading) {
        return (
            <div className="card card-custom bg-white shadow-sm mb-5 p-6 border-0">
                <div className="d-flex flex-column align-items-center justify-content-center py-5">
                    <div className="spinner-border text-warning" role="status"></div>
                    <span className="text-gray-400 fw-semibold fs-7 mt-3">Analisi mercato...</span>
                </div>
            </div>
        )
    }

    // 2. Insight NOT available
    if (!isAvailable || !data) {
        return (
            <div className="card card-custom bg-white shadow-sm mb-5 p-6 border-0">
                <div className="d-flex flex-column justify-content-center align-items-center">
                    <i className="ki-outline ki-eye fs-2x text-warning mb-3"></i>
                    <span className="text-gray-800 fw-bold fs-6 mb-1">Insight di mercato</span>
                    <span className="text-gray-400 fw-semibold fs-7">Prossima attivazione</span>
                </div>
            </div>
        )
    }

    // 3. Insight Available
    const { trend, summary, generatedAt } = data
    const trendConfig = getTrendConfig(trend)

    // Heuristic: show expand if longer than 100 chars
    const isLongText = (summary?.length || 0) > 100

    return (
        <>
            <div className="card card-custom bg-white shadow-sm mb-5 border-0" style={{ position: 'relative' }}>
                <div className="card-body p-6">
                    {/* Header: Title + Trend Badge */}
                    <div className="d-flex justify-content-between align-items-start mb-4">
                        <div className="d-flex align-items-center">
                            <i className="ki-outline ki-eye fs-2x text-warning me-3"></i>
                            <div>
                                <div className="text-gray-800 fw-bold fs-6">Insight di mercato</div>
                                {generatedAt && (
                                    <div className="text-gray-400 fs-8 fw-semibold">
                                        Aggiornato: {formatDate(generatedAt)}
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Badge Trend */}
                        {trend && (
                            <span className={`badge badge-light-${trendConfig.color} fw-bold fs-8 d-flex align-items-center`}>
                                {trendConfig.label}
                            </span>
                        )}
                    </div>

                    {/* Content: Summary */}
                    <div className="mb-0 position-relative">
                        <p className="text-gray-800 fs-7 fw-semibold lh-base mb-0" style={{
                            display: '-webkit-box',
                            WebkitLineClamp: 3,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden'
                        }}>
                            {summary}
                        </p>
                    </div>

                    {/* Expand Icon - Bottom Right */}
                    {isLongText && (
                        <div
                            className="position-absolute bottom-0 end-0 p-4"
                            style={{ zIndex: 10 }}
                        >
                            <div
                                className="d-flex align-items-center justify-content-center bg-warning rounded-circle shadow-sm hover-scale"
                                style={{ width: '25px', height: '25px', cursor: 'pointer' }}
                                onClick={() => setShowDetail(true)}
                                title="Approfondisci"
                            >
                                <i className="ki-outline ki-arrow-up-right fs-4 text-white"></i>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* Light View - Simple Overlay Modal */}
            {showDetail && (
                <div className="modal fade show d-block" tabIndex={-1} style={{ backgroundColor: 'rgba(0,0,0,0.4)', zIndex: 9999 }}>
                    <div className="modal-dialog modal-dialog-centered mw-650px">
                        <div className="modal-content shadow-lg">
                            <div className="modal-header pb-0 border-0 justify-content-end">
                                <div className="btn btn-sm btn-icon btn-active-color-primary" onClick={() => setShowDetail(false)}>
                                    <i className="ki-outline ki-cross fs-1"></i>
                                </div>
                            </div>

                            <div className="modal-body pt-0 pb-15 px-5 px-xl-10">
                                {/* Modal Header Content */}
                                <div className="mb-5">
                                    <div className="d-flex justify-content-between align-items-center mb-5">
                                        <div className="d-flex align-items-center">
                                            <i className="ki-outline ki-eye fs-2x text-warning me-4"></i>
                                            <h2 className="fw-bold text-gray-900 mb-0">Insight di mercato</h2>
                                        </div>
                                        {trend && (
                                            <span className={`badge badge-light-${trendConfig.color} fw-bold fs-7`}>
                                                {trendConfig.label}
                                            </span>
                                        )}
                                    </div>
                                    {generatedAt && (
                                        <div className="text-gray-500 fw-semibold fs-6">
                                            Generato il {formatDate(generatedAt)}
                                        </div>
                                    )}
                                </div>

                                {/* Full Text */}
                                <div className="text-gray-800 fs-5 fw-semibold lh-base text-break">
                                    {summary}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </>
    )
}
